import { COUNTRIES } from '@config/constants'
import { ROUTES } from '@router'

import translations from './Footer.translations'

const INSTAGRAM_URL = 'https://www.instagram.com/'
const FACEBOOK_URL = 'https://www.facebook.com/'
const TWITTER_URL = 'https://www.twitter.com/'

export const TYPES = {
  LINK_INTERNAL: 'link-internal',
  LINK: 'link',
  LINK_EXTERNAL: 'link-external',
  FILE: 'file',
  PAYMENT_NETWORKS: 'payment-networks',
  SOCIAL_NETWORKS: 'social-networks',
  ICONS: 'icons',
}

/*
  key: translation key on Lokalise
  title: direct label to display (no key provided)
  name: name of the link, if the key does not exist (when we only have a title)
  prefetch: If the page behind the link should be prefetched. Defaults to true
  url: url of the link. Usually provided as a function since the links are different regarding the locale.
  type:
    - link: classic link on BM
    - link-external: target blank on a link to open another website
    - file: link to a file with target blank
    - payment-methods: display the different payment methods available in the current country
*/
const LINKS = {
  ABOUT_US: {
    key: translations.aboutUs,
    name: ROUTES.ABOUT_US,
    type: TYPES.LINK_INTERNAL,
  },
  ACCESSIBILITY: {
    key: translations.accessibilityStatement,
    name: ROUTES.A11Y.STATEMENT,
    type: TYPES.LINK_INTERNAL,
  },
  MODELS_COMPARISON: {
    key: translations.modelsComparison,
    // [ACQ-775] TO DO: Replace with Models
    // Comparison Home route when it's ready.
    name: ROUTES.MODELS_COMPARISON.CATEGORY,
    params: {
      category: 'smartphones',
    },
    prefetch: false,
    type: TYPES.LINK_INTERNAL,
  },
  BUY_BACK: {
    key: translations.buyback,
    name: ROUTES.CMS.BUYBACK,
    params: {
      pageName: 'home',
    },
    type: TYPES.LINK_INTERNAL,
  },
  BUY_BACK_B2B: (url) => ({
    key: translations.buybackB2B,
    url,
    type: TYPES.LINK_EXTERNAL,
  }),
  BUYING_GUIDES: {
    key: translations.buyingGuides,
    name: ROUTES.BUYING_GUIDE.ROOT_HUB,
    type: TYPES.LINK_INTERNAL,
  },
  STUDENTS_GOOD_DEALS: {
    key: translations.studentGoodDeals,
    name: ROUTES.STUDENT_DISCOUNT,
    type: TYPES.LINK_INTERNAL,
  },
  MILITARY_PROGRAM: {
    key: translations.militaryProgram,
    name: ROUTES.MILITARY_DISCOUNT,
    type: TYPES.LINK_INTERNAL,
  },
  HIRE: (url) => ({ key: translations.hire, url, type: TYPES.LINK_EXTERNAL }),
  BLOG: (url) => ({ key: translations.blog, url, type: TYPES.LINK_EXTERNAL }),
  PRESS: (url) => ({ key: translations.press, url, type: TYPES.LINK }),
  BACKMARKET_WARRANTY: {
    key: translations.backmarketWarranty,
    name: ROUTES.PDF.WARRANTY,
    type: TYPES.LINK_INTERNAL,
  },
  BECOME_REFURBISHER: (url) => ({
    key: translations.becomeRefurbisher,
    url,
    type: TYPES.LINK_EXTERNAL,
  }),
  BO_MERCHANT: {
    key: translations.boMerchant,
    url: '/bo_merchant',
    type: TYPES.LINK,
  },
  PAYMENT: (url) => ({ key: translations.payment, url, type: TYPES.LINK }),
  SHIPPING: (url) => ({ key: translations.shipping, url, type: TYPES.LINK }),
  HELP: {
    key: translations.help,
    name: ROUTES.HELP_CENTER.HOME,
    type: TYPES.LINK_INTERNAL,
  },
  TEL: (url, title) => ({ name: 'footer_tel', title, url, type: TYPES.LINK }),
  // Category: Law
  TERMS: {
    key: translations.terms,
    name: ROUTES.PDF.TERMS_USE,
    type: TYPES.LINK_INTERNAL,
  },
  TERMS_SALE: {
    key: translations.termsSale,
    name: ROUTES.PDF.TERMS_SALE,
    type: TYPES.LINK_INTERNAL,
  },
  TERMS_BUYBACK: {
    key: translations.termsBuyback,
    name: ROUTES.PDF.TERMS_BUYBACK,
    type: TYPES.LINK_INTERNAL,
  },
  DATA_PROTECTION: {
    key: translations.dataProtection,
    name: ROUTES.LEGAL.DATA_PROTECTION,
    type: TYPES.LINK_INTERNAL,
  },
  COOKIE_POLICY: {
    key: translations.cookies,
    name: ROUTES.LEGAL.COOKIES,
    type: TYPES.LINK_INTERNAL,
  },
  INFOS_CONSUMER: {
    key: translations.infosConsumer,
    name: ROUTES.PDF.CONSUMER,
    type: TYPES.LINK_INTERNAL,
  },
  LEGAL: {
    key: translations.legal,
    name: ROUTES.LEGAL.MENTIONS,
    type: TYPES.LINK_INTERNAL,
  },
  // Category: Hello
  TRUST_PILOT: (url) => ({
    url,
    key: translations.trustpilot,
    type: TYPES.LINK_EXTERNAL,
  }),
  TRUSTED_SHOPS: (url) => ({
    url,
    key: translations.trustedShops,
    type: TYPES.LINK_EXTERNAL,
  }),
  GLASSDOOR: (url) => ({
    url,
    key: translations.glassdoor,
    type: TYPES.LINK_EXTERNAL,
  }),
  WELCOME_TO_THE_JUNGLE: {
    key: translations.welcomeToTheJungle,
    url: 'https://www.welcometothejungle.co/fr/companies/back-market',
    type: TYPES.LINK_EXTERNAL,
  },
  MEDIUM: {
    key: translations.medium,
    url: 'https://medium.com/@BackMarket',
    type: TYPES.LINK_EXTERNAL,
  },
  CELEBRITY: (url) => ({
    key: translations.celebrity,
    url,
    type: TYPES.LINK_EXTERNAL,
  }),
}

const DISPLAY = {
  PAYMENT_NETWORKS: {
    key: translations.paymentMethods,
    type: TYPES.PAYMENT_NETWORKS,
  },
  SOCIAL_NETWORKS: {
    type: TYPES.SOCIAL_NETWORKS,
  },
  ICONS: ({ url, icons, style }) => ({
    type: TYPES.ICONS,
    url,
    icons,
    style,
  }),
}

const footerRules = {
  [COUNTRIES.BE]: {
    socials: {
      twitter: `${TWITTER_URL}back_market_be`,
      linkedin: 'https://be.linkedin.com/company/back-market',
      facebook: `${FACEBOOK_URL}pages/Back-Market/1497704560478521`,
      instagram: `${INSTAGRAM_URL}backmarket_fr`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BLOG('https://story.backmarket.fr'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BACKMARKET_WARRANTY,
          LINKS.BECOME_REFURBISHER('https://backmarket.be/fr-be/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.INFOS_CONSUMER,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/fr-be/articles/360026643854-Quels-sont-les-moyens-de-paiement-accept%C3%A9s-sur-Back-Market',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/fr-be/articles/360026646054-Dans-quel-pays-puis-je-me-faire-livrer-et-combien-cela-me-co%C3%BBte',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT('https://fr.trustpilot.com/review/backmarket.be'),
          LINKS.GLASSDOOR(
            'https://fr.glassdoor.be/Pr%C3%A9sentation/Travailler-chez-Back-Market-EI_IE1780208.16,27.htm',
          ),
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://fr.wikipedia.org/wiki/Nana_Mouskouri'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.DE]: {
    socials: {
      twitter: `${TWITTER_URL}back_market_de`,
      linkedin: 'https://de.linkedin.com/company/back-market',
      facebook: `${FACEBOOK_URL}backmarketdeutschland`,
      instagram: `${INSTAGRAM_URL}backmarket_de`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BLOG('https://story.backmarket.de'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BACKMARKET_WARRANTY,
          LINKS.BECOME_REFURBISHER('https://backmarket.de/de-de/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/de/articles/360026643854-Welche-Zahlungsarten-gibt-es-auf-Back-Market',
          ),
          LINKS.HELP,
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/de/articles/360026646054-In-welches-Land-kann-ich-liefern-lassen-und-wie-viel-kostet-mich-das',
          ),
          DISPLAY.ICONS({
            url: 'https://help.backmarket.com/hc/de/articles/360026646054-In-welches-Land-kann-ich-liefern-lassen-und-wie-viel-kostet-mich-das?_ga=2.153616504.1908722119.1593102993-1001137500.1588685654',
            icons: [
              {
                src: 'delivery_companies/ups.svg',
                width: 52,
                height: 62,
              },
              {
                src: 'delivery_companies/dhl.svg',
                width: 2500,
                height: 1595,
              },
              {
                src: 'delivery_companies/dpd.svg',
                width: 300,
                height: 126,
              },
            ],
            style: 'iconSmall',
          }),
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.TERMS_BUYBACK,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT(
            'https://fr.trustpilot.com/review/www.backmarket.de',
          ),
          LINKS.GLASSDOOR(
            'https://www.glassdoor.com/Overview/Working-at-Back-Market-EI_IE1780208.11,22.htm',
          ),
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://de.wikipedia.org/wiki/Nana_Mouskouri'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.AT]: {
    socials: {
      facebook: `${FACEBOOK_URL}backmarketat`,
      instagram: `${INSTAGRAM_URL}backmarket_at`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
          LINKS.STUDENTS_GOOD_DEALS,
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BACKMARKET_WARRANTY,
          LINKS.BECOME_REFURBISHER('https://backmarket.at/de-at/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/de-at/articles/360026643854-Welche-Zahlungsarten-gibt-es-auf-Back-Market-',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/de-at/articles/360026646054-In-welches-Land-kann-ich-liefern-lassen-und-wie-viel-kostet-mich-das-',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.GLASSDOOR(
            'https://www.glassdoor.at/%C3%9Cberblick/Arbeit-bei-Back-Market-EI_IE1780208.11,22.htm?countryRedirect=true',
          ),
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://de.wikipedia.org/wiki/DJ_%C3%96tzi'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.ES]: {
    socials: {
      twitter: `${TWITTER_URL}back_market_es`,
      linkedin: 'https://es.linkedin.com/company/back-market',
      facebook: `${FACEBOOK_URL}Back-Market-España-196051144097157`,
      instagram: `${INSTAGRAM_URL}backmarket_es`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BLOG('https://story.backmarket.es'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
          DISPLAY.ICONS({
            url: 'https://www.confianzaonline.es/empresas/backmarket.htm',
            icons: [
              {
                src: 'footer/confianza-online.svg',
                width: 150,
                height: 150,
              },
              {
                src: 'footer/confianza-europe.svg',
                width: 150,
                height: 150,
              },
            ],
            style: 'icon',
          }),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BECOME_REFURBISHER('https://backmarket.es/es-es/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/es/articles/360026643854--Qu%C3%A9-m%C3%A9todos-de-pago-se-aceptan-en-Back-Market',
          ),
          LINKS.BACKMARKET_WARRANTY,
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/es/articles/360026646054--En-qu%C3%A9-pa%C3%ADs-me-pueden-entregar-y-cu%C3%A1nto-me-cuesta',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT(
            'https://es.trustpilot.com/review/www.backmarket.es',
          ),
          LINKS.GLASSDOOR(
            'https://www.glassdoor.es/Resumen/Trabajar-en-Back-Market-EI_IE1780208.12,23.htm',
          ),
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://es.wikipedia.org/wiki/Ángela_Ruiz_Robles'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.FR]: {
    socials: {
      twitter: `${TWITTER_URL}back_market`,
      linkedin: 'https://fr.linkedin.com/company/back-market',
      facebook: `${FACEBOOK_URL}pages/Back-Market/1497704560478521`,
      instagram: `${INSTAGRAM_URL}backmarket_fr/`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.BUY_BACK,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BLOG('https://story.backmarket.fr'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BACKMARKET_WARRANTY,
          LINKS.BECOME_REFURBISHER('https://backmarket.fr/fr-fr/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.INFOS_CONSUMER,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/fr-fr/articles/360026643854-Quels-sont-les-moyens-de-paiement-accept%C3%A9s-sur-Back-Market',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/fr-fr/articles/360026646054-Dans-quel-pays-puis-je-me-faire-livrer-et-combien-cela-me-co%C3%BBte',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.TERMS_BUYBACK,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT(
            'https://fr.trustpilot.com/review/www.backmarket.fr',
          ),
          LINKS.GLASSDOOR(
            'https://www.glassdoor.com/Overview/Working-at-Back-Market-EI_IE1780208.11,22.htm',
          ),
          LINKS.WELCOME_TO_THE_JUNGLE,
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://fr.wikipedia.org/wiki/Nana_Mouskouri'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.FI]: {
    socials: {
      facebook: `${FACEBOOK_URL}backmarketfi`,
      instagram: `${INSTAGRAM_URL}backmarket_fi`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BLOG('https://story.backmarket.fi'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BECOME_REFURBISHER('https://backmarket.fi/fi-fi/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/fi/articles/360026643854',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/fi/articles/360026646054',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT(
            'https://fi.trustpilot.com/review/backmarket.fi?languages=all',
          ),
          LINKS.GLASSDOOR(
            'https://www.glassdoor.com/Reviews/Back-Market-Reviews-E1780208_P3.html',
          ),
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://fi.wikipedia.org/wiki/M%C3%A4mmi'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.GR]: {
    socials: {
      twitter: `${TWITTER_URL}back_market`,
      linkedin: 'https://fr.linkedin.com/company/back-market',
      facebook: `${FACEBOOK_URL}backmarketgr`,
      instagram: `${INSTAGRAM_URL}backmarket_gr/`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BECOME_REFURBISHER('https://backmarket.gr/el-gr/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/el/articles/360026643854',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/el/articles/360034275533',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT('https://gr.trustpilot.com/review/backmarket.gr'),
          LINKS.CELEBRITY(
            'https://el.wikipedia.org/wiki/%CE%9D%CF%84%CE%AD%CE%BC%CE%B7%CF%82_%CE%A1%CE%BF%CF%8D%CF%83%CF%83%CE%BF%CF%82',
          ),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.IT]: {
    socials: {
      twitter: `${TWITTER_URL}back_market_it`,
      linkedin: 'https://it.linkedin.com/company/back-market',
      facebook: `${FACEBOOK_URL}backmarketitalia`,
    },

    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.BLOG('https://story.backmarket.it'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BACKMARKET_WARRANTY,
          LINKS.BECOME_REFURBISHER('https://backmarket.it/it-it/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/it/articles/360026643854-Quali-sono-i-metodi-di-pagamento-accettati-da-Back-Market',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/it/articles/360026646054-In-quale-paese-posso-essere-consegnato-e-quanto-costa',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUSTED_SHOPS('https://it.trustpilot.com/review/backmarket.it'),
          LINKS.GLASSDOOR(
            'https://www.glassdoor.it/Panoramica/Lavorando-in-Back-Market-EI_IE1780208.13,24.htm',
          ),
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://it.wikipedia.org/wiki/Raffaella_Carr%C3%A0'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.US]: {
    socials: {
      twitter: `${TWITTER_URL}backmarket`,
      linkedin: 'https://linkedin.com/company/back-market',
      facebook: `${FACEBOOK_URL}BackMarketCom`,
      instagram: `${INSTAGRAM_URL}backmarket`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.BUY_BACK,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.MILITARY_PROGRAM,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BLOG('https://story.backmarket.com'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('https://backmarket.com/press.html'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BECOME_REFURBISHER('https://backmarket.com/en-us/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/en-us/articles/360026643854-What-payment-methods-are-accepted',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/en-us/articles/360033250174-When-should-I-expect-my-order-to-arrive',
          ),
          LINKS.BACKMARKET_WARRANTY,
          LINKS.HELP,
          LINKS.ACCESSIBILITY,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_BUYBACK,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT(
            'https://www.trustpilot.com/review/www.backmarket.com',
          ),
          LINKS.GLASSDOOR(
            'https://www.glassdoor.com/Overview/Working-at-Back-Market-EI_IE1780208.11,22.htm',
          ),
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://en.wikipedia.org/wiki/Dwayne_Johnson'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.GB]: {
    socials: {
      linkedin: 'https://linkedin.com/company/back-market',
      facebook: `${FACEBOOK_URL}backmarketuk`,
      instagram: `${INSTAGRAM_URL}backmarket_uk`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BLOG('https://story.backmarket.co.uk'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('https://backmarket.com/press.html'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BACKMARKET_WARRANTY,
          LINKS.BECOME_REFURBISHER(
            'https://backmarket.co.uk/en-gb/seller/home',
          ),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/en-gb/articles/360026643854-What-payment-methods-are-accepted-on-Back-Market',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/en-gb/articles/360026646054-Where-can-I-get-delivered-and-how-much-does-it-cost-me',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT(
            'https://www.trustpilot.com/review/www.backmarket.co.uk',
          ),
          LINKS.GLASSDOOR(
            'https://www.glassdoor.co.uk/Overview/Working-at-Back-Market-EI_IE1780208.11,22.htm',
          ),
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://en.wikipedia.org/wiki/Deep-fried_Mars_bar'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.NL]: {
    socials: {
      facebook: `${FACEBOOK_URL}backmarketnl`,
      instagram: `${INSTAGRAM_URL}backmarket_nl`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BLOG('https://story.thebackmarket.nl'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('https://backmarket.com/press.html'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BACKMARKET_WARRANTY,
          LINKS.BECOME_REFURBISHER('https://backmarket.nl/nl-nl/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/nl/articles/360026643854-Hoe-kan-ik-op-Back-Market-betalen-',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/nl/articles/360033250174-Wanneer-wordt-mijn-bestelling-bezorgd-',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT(
            'https://www.trustpilot.com/review/www.backmarket.nl',
          ),
          LINKS.GLASSDOOR(
            'https://www.glassdoor.nl/Overzicht/Werken-bij-Back-Market-EI_IE1780208.11,22.htm',
          ),
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://nl.wikipedia.org/wiki/K3_(muziekgroep)'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.PT]: {
    socials: {
      facebook: `${FACEBOOK_URL}backmarketpt`,
      instagram: `${INSTAGRAM_URL}backmarket_pt/`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BLOG('https://story.backmarket.pt'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BECOME_REFURBISHER('https://backmarket.pt/pt-pt/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/pt/articles/360026643854',
          ),
          LINKS.BACKMARKET_WARRANTY,
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/pt/articles/360026646054',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT(
            'https://pt.trustpilot.com/review/backmarket.pt?languages=all',
          ),
          LINKS.MEDIUM,
          LINKS.CELEBRITY(
            'https://fr.wikipedia.org/wiki/Am%C3%A1lia_Rodrigues',
          ),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.IE]: {
    socials: {
      facebook: `${FACEBOOK_URL}backmarketie`,
      instagram: `${INSTAGRAM_URL}backmarket_ie`,
      linkedin: 'https://linkedin.com/company/back-market',
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          // TODO reactivate when blog is ready
          // LINKS.BLOG('https://story.backmarket.ie/'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BACKMARKET_WARRANTY,
          LINKS.BECOME_REFURBISHER('https://backmarket.ie/en-ie/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/en-ie/articles/360026643854-What-payment-methods-are-accepted-on-Back-Market-',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/en-ie/articles/360026646054-Where-can-I-get-delivered-and-how-much-does-it-cost-me-',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          // TODO: Missing link for trust pilot: LINKS.TRUST_PILOT()
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://en.wikipedia.org/wiki/Dustin_the_Turkey'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.SK]: {
    socials: {
      facebook: `${FACEBOOK_URL}backmarketsk`,
      instagram: `${INSTAGRAM_URL}backmarket_sk`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('mailto:press@backmarket.com'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BECOME_REFURBISHER('https://backmarket.sk/sk-sk/seller/home'),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/sk/articles/360026643854',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/sk/articles/360026646054',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.MEDIUM,
          LINKS.CELEBRITY('https://sk.wikipedia.org/wiki/Peter_Sagan'),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.SE]: {
    socials: {
      facebook: `${FACEBOOK_URL}backmarketse`,
      instagram: `${INSTAGRAM_URL}backmarket_se`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.PRESS('mailto:press@backmarket.com'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BECOME_REFURBISHER('https://backmarket.se/sv-se/seller/home '),
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT(
            'https://help.backmarket.com/hc/sv/articles/360026643854',
          ),
          LINKS.SHIPPING(
            'https://help.backmarket.com/hc/sv/articles/360034275533',
          ),
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.TERMS_SALE,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          LINKS.LEGAL,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.TRUST_PILOT('https://www.trustpilot.com/review/backmarket.se'),
          LINKS.MEDIUM,
          LINKS.CELEBRITY(
            'https://sv.wikipedia.org/wiki/Zlatan_Ibrahimovi%C4%87',
          ),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
  [COUNTRIES.JP]: {
    socials: {
      twitter: `${TWITTER_URL}backmarket_jp`,
      linkedin: 'https://jp.linkedin.com/company/back-market',
      facebook: `${FACEBOOK_URL}backmarketjapan`,
      instagram: `${INSTAGRAM_URL}backmarket_jp`,
    },
    categories: [
      {
        title: translations.categoryAbout,
        content: [
          LINKS.ABOUT_US,
          LINKS.MODELS_COMPARISON,
          LINKS.STUDENTS_GOOD_DEALS,
          LINKS.HIRE('https://jobs.backmarket.com'),
          LINKS.BLOG('https://story.backmarket.co.jp'),
          LINKS.BUYING_GUIDES,
          LINKS.PRESS('https://backmarket.com/press.html'),
        ],
      },
      {
        title: translations.categoryHelp,
        content: [
          LINKS.BO_MERCHANT,
          LINKS.PAYMENT('https://www.backmarket.co.jp/faq?id=q1-6'),
          LINKS.SHIPPING('https://www.backmarket.co.jp/faq?id=q1-9'),
          LINKS.BACKMARKET_WARRANTY,
          LINKS.HELP,
        ],
      },
      {
        title: translations.categoryLaw,
        content: [
          LINKS.TERMS,
          LINKS.DATA_PROTECTION,
          LINKS.COOKIE_POLICY,
          DISPLAY.PAYMENT_NETWORKS,
        ],
      },
      {
        title: translations.categoryHello,
        content: [
          LINKS.MEDIUM,
          LINKS.CELEBRITY(
            'https://ja.wikipedia.org/wiki/%E3%83%89%E3%83%A9%E3%81%88%E3%82%82%E3%82%93',
          ),
          DISPLAY.SOCIAL_NETWORKS,
        ],
      },
    ],
  },
}

export const getFooterConfigFor = (country) => footerRules[country]
export const getSocialNetWorksFor = (country) =>
  getFooterConfigFor(country).socials || {}
